import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from '@/components/formatted_message/FormattedMessage';
import {
  CompressedAirRegister,
  ElectricityRegister,
  GasRegister,
  ThermalRegister,
  OtherRegister,
  RegisterMedium,
  WaterRegister,
} from '@enmon/commons';
import { APP_DICTIONARY } from '../../root';

const TRANSLATED_GAS_REGISTERS = defineMessages<GasRegister>({
  [GasRegister.AccumulatedVolumeForwardTotal]: { id: 'app.common.meter.register.Gas.AccumulatedVolumeForwardTotal' },
  [GasRegister.AccumulatedVolumeForwardTemperatureConvertedTotal]: {
    id: 'app.common.meter.register.Gas.AccumulatedVolumeForwardTemperatureConvertedTotal',
  },
  [GasRegister.AccumulatedVolumeForwardBaseConditionsTotal]: {
    id: 'app.common.meter.register.Gas.AccumulatedVolumeForwardBaseConditionsTotal',
  },
  [GasRegister.BaseTemperature]: { id: 'app.common.meter.register.Gas.BaseTemperature' },
  [GasRegister.BasePressure]: { id: 'app.common.meter.register.Gas.BasePressure' },
});

const TRANSLATED_WATER_REGISTERS = defineMessages<WaterRegister>({
  [WaterRegister.AccumulatedVolumeTotal]: { id: 'app.common.meter.register.Water.AccumulatedVolumeTotal' },
  [WaterRegister.FlowRateAverage]: { id: 'app.common.meter.register.Water.FlowRateAverage' },
});

const TRANSLATED_THERMAL_REGISTERS = defineMessages<ThermalRegister>({
  [ThermalRegister.AccumulatedEnergyTotal]: { id: 'app.common.meter.register.Thermal.AccumulatedEnergyTotal' },
  [ThermalRegister.AccumulatedVolumeTotal]: { id: 'app.common.meter.register.Thermal.AccumulatedVolumeTotal' },
  [ThermalRegister.PowerAverage]: { id: 'app.common.meter.register.Thermal.PowerAverage' },
  [ThermalRegister.FlowRateAverage]: { id: 'app.common.meter.register.Thermal.FlowRateAverage' },
  [ThermalRegister.FlowTemperatureCurrent]: { id: 'app.common.meter.register.Thermal.FlowTemperatureCurrent' },
  [ThermalRegister.ReturnTemperatureCurrent]: {
    id: 'app.common.meter.register.Thermal.ReturnTemperatureCurrent',
  },
});

const TRANSLATED_OTHER_REGISTERS = defineMessages<OtherRegister>({
  [OtherRegister.Temperature]: { id: 'app.common.meter.register.Other.Temperature' },
  [OtherRegister.Humidity]: { id: 'app.common.meter.register.Other.Humidity' },
  [OtherRegister.Pressure]: { id: 'app.common.meter.register.Other.Pressure' },
  [OtherRegister.CO2]: { id: 'app.common.meter.register.Other.CO2' },
  [OtherRegister.Wind]: { id: 'app.common.meter.register.Other.Wind' },
  [OtherRegister.Noise]: { id: 'app.common.meter.register.Other.Noise' },
  [OtherRegister.Light]: { id: 'app.common.meter.register.Other.Light' },
  [OtherRegister.Level]: { id: 'app.common.meter.register.Other.Level' },
  [OtherRegister.Precipitation]: { id: 'app.common.meter.register.Other.Precipitation' },
  [OtherRegister.BinarySwitch]: { id: 'app.common.meter.register.Other.BinarySwitch' },
  [OtherRegister.BatteryVoltage]: { id: 'app.common.meter.register.Other.BatteryVoltage' },
  [OtherRegister.Signal]: { id: 'app.common.meter.register.Other.Signal' },
  [OtherRegister.BatteryCharge]: { id: 'app.common.meter.register.Other.BatteryCharge' },
});

export const TRANSLATED_OBIS: Readonly<
  Record<RegisterMedium, undefined | Readonly<Record<string, MessageDescriptor>>>
> = {
  [RegisterMedium.PressedAir]: defineMessages<CompressedAirRegister>({
    [CompressedAirRegister.LoadProfileOperatingTotal]: {
      id: 'app.common.meter.register.CompressedAir.LoadProfileOperatingTotal',
    },
    [CompressedAirRegister.LoadProfileStandardTotal]: {
      id: 'app.common.meter.register.CompressedAir.LoadProfileStandardTotal',
    },
  }),
  [RegisterMedium.Electricity]: defineMessages<ElectricityRegister>({
    [ElectricityRegister.PositiveActiveDemandInLastCompletedDemandPeriod]: {
      id: 'app.common.meter.register.Electricity.PositiveActiveDemandInLastCompletedDemandPeriod',
    },
    [ElectricityRegister.PositiveEnergyTotal]: { id: 'app.common.meter.register.Electricity.PositiveEnergyTotal' },
    [ElectricityRegister.NegativeEnergyTotal]: { id: 'app.common.meter.register.Electricity.NegativeEnergyTotal' },
    [ElectricityRegister.PositiveReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.PositiveReactiveTotal',
    },
    [ElectricityRegister.NegativeReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.NegativeReactiveTotal',
    },
    [ElectricityRegister.ImportedInductiveReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.ImportedInductiveReactiveTotal',
    },
    [ElectricityRegister.ImportedCapacitiveReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.ImportedCapacitiveReactiveTotal',
    },
    [ElectricityRegister.ExportedInductiveReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.ExportedInductiveReactiveTotal',
    },
    [ElectricityRegister.ExportedCapacitiveReactiveTotal]: {
      id: 'app.common.meter.register.Electricity.ExportedCapacitiveReactiveTotal',
    },
    [ElectricityRegister.InstantaneousCurrentPhase1]: {
      id: 'app.common.meter.register.Electricity.InstantaneousCurrentPhase1',
    },
    [ElectricityRegister.InstantaneousCurrentPhase2]: {
      id: 'app.common.meter.register.Electricity.InstantaneousCurrentPhase2',
    },
    [ElectricityRegister.InstantaneousCurrentPhase3]: {
      id: 'app.common.meter.register.Electricity.InstantaneousCurrentPhase3',
    },
    [ElectricityRegister.MaximumCurrentPhase1]: { id: 'app.common.meter.register.Electricity.MaximumCurrentPhase1' },
    [ElectricityRegister.MaximumCurrentPhase2]: { id: 'app.common.meter.register.Electricity.MaximumCurrentPhase2' },
    [ElectricityRegister.MaximumCurrentPhase3]: { id: 'app.common.meter.register.Electricity.MaximumCurrentPhase3' },
    [ElectricityRegister.InstantaneousVoltagePhase1]: {
      id: 'app.common.meter.register.Electricity.InstantaneousVoltagePhase1',
    },
    [ElectricityRegister.InstantaneousVoltagePhase2]: {
      id: 'app.common.meter.register.Electricity.InstantaneousVoltagePhase2',
    },
    [ElectricityRegister.InstantaneousVoltagePhase3]: {
      id: 'app.common.meter.register.Electricity.InstantaneousVoltagePhase3',
    },
    [ElectricityRegister.MaximumVoltagePhase1]: { id: 'app.common.meter.register.Electricity.MaximumVoltagePhase1' },
    [ElectricityRegister.MaximumVoltagePhase2]: { id: 'app.common.meter.register.Electricity.MaximumVoltagePhase2' },
    [ElectricityRegister.MaximumVoltagePhase3]: { id: 'app.common.meter.register.Electricity.MaximumVoltagePhase3' },
    [ElectricityRegister.MaximumPositiveActivePower]: {
      id: 'app.common.meter.register.Electricity.MaximumPositiveActivePower',
    },
    [ElectricityRegister.MaximumPositiveActivePowerPhase1]: {
      id: 'app.common.meter.register.Electricity.MaximumPositiveActivePowerPhase1',
    },
    [ElectricityRegister.MaximumPositiveActivePowerPhase2]: {
      id: 'app.common.meter.register.Electricity.MaximumPositiveActivePowerPhase2',
    },
    [ElectricityRegister.MaximumPositiveActivePowerPhase3]: {
      id: 'app.common.meter.register.Electricity.MaximumPositiveActivePowerPhase3',
    },
    [ElectricityRegister.PowerFactor]: APP_DICTIONARY.powerFactor,
  }),
  [RegisterMedium.Gas]: TRANSLATED_GAS_REGISTERS,
  [RegisterMedium.Nitrogen]: TRANSLATED_GAS_REGISTERS,
  [RegisterMedium.Heating]: TRANSLATED_THERMAL_REGISTERS,
  [RegisterMedium.Cold]: TRANSLATED_THERMAL_REGISTERS,
  [RegisterMedium.WarmWater]: TRANSLATED_WATER_REGISTERS,
  [RegisterMedium.ColdWater]: TRANSLATED_WATER_REGISTERS,
  [RegisterMedium.Other]: TRANSLATED_OTHER_REGISTERS,
  [RegisterMedium.Custom]: undefined,
  [RegisterMedium.Oil]: undefined,
};
